<template>
    <div id="main">
        <div class="page" v-show="!rotaAninhada">
            <div class="tipos-servicos vertical-flow">
                <div class="listing-header">
                    <div class="title-breadcrumb">
                        <h1 class="title">
                            {{ $t('ocorrencia') }}
                        </h1>
                        <nav class="breadcrumb" aria-label="breadcrumbs">
                            <ul>
                                <li>
                                    <a class="a-inativo">{{ 
                                        isAgro ?
                                        $t("agro") :
                                        $t("vendas")
                                    }}</a>
                                </li>
                                <li>
                                    <a aria-current="page">{{ 
                                        isAgro ?
                                        $t('ocorrencia') :
                                        $t("crm")
                                        }}</a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                    <div class="header-buttons">
                        <button
                            v-if="!integracao && permissao.create"
                            class="save-btn"
                            type="button"
                            tag="button"
                            @click="$router.push({ name: `${abrev}-ocorrencia-new` })"
                        >
                            {{ $t("adicionar") }}
                        </button>
                    </div>
                </div>

                <div class="columns">
                    <div class="column is-5">
                        <search-bar :onSearch="debouncedOnSearch" tooltipLabel="msg-campo-busca-ocorrencia"/>
                    </div>
                    <div class="column is-4">
                        <vss-select
                            ref="tipoSelect"
                            title="tipo"
                            placeholder="selecione"
                            :options="tipoSelect"
                            v-model="tipoSelected"
                            @input="onTipoChanged"
                            :height="40"
                            :clearable="true"
                        />
                    </div>
                    <div class="column is-3">
                        <vss-select
                            ref="situacaoInput"
                            title="situacao"
                            :options="situacaoSelect"
                            :mlLabels="true"
                            @input="onSituacaoChanged"
                            v-model="situacaoSelected"
                            :height="40"
                            :clearable="true"
                        />
                    </div>
                </div>

                <div class="registry-wrapper">
                    <container-loader v-if="isLoading && !$store.getters.isLoading" />
                    <empty-table-message v-if="isEmpty" />
                    <b-table
                        v-else
                        :data="ocorrencias"
                        class="data-table"
                        @sort="sortTable"
                        backend-sorting
                    >
                        <b-table-column
                            v-for="(f, i) in fields"
                            :key="i"
                            :field="f.key"
                            :label="f.key != 'excluir' ? $t(f.key) : ''"
                            :width="f.width"
                            :class="{'excluir-field': f.key == 'excluir' }"
                            :sortable="f.sortable"
                            :custom-sort="f.sortFn"
                            v-slot="props"
                        >
                            <div v-if="f.key == 'nomepopular' || f.key == 'nomecientifico'">
                                <router-link
                                    v-if="permissao.read"
                                    :to="{
                                        name: `${abrev}-ocorrencia-edit`,
                                        params: { id: props.row.id },
                                    }"
                                >
                                    <pre>{{ props.row[f.key] }}</pre>
                                </router-link>
                                <span v-else>{{ props.row[f.key] }}</span>
                            </div>
                            <div v-else-if="f.key == 'excluir'">
                                <span v-if="permissao.update && !integracao">
                                    <b-button
                                        class="action-button"
                                        :type="props.row['situacao'] == 1 ? 'is-danger' : 'is-success'"
                                        :icon-right="props.row['situacao'] == 1 ? 'delete' : 'check'"
                                        @click="promptChangeSituacao(props.row)"
                                        v-tippy 
                                        :title="$t(props.row['situacao'] == 1 ? 'desativar' : 'reativar')"
                                    />
                                </span>
                            </div>
                            <div v-else-if="f.key == 'situacao'">
                                <b-tag
                                    class="table-tag table-tag-full-width"
                                    v-if="props.row[f.key] == 1"
                                    type="is-success"
                                    size="is-medium"
                                    >{{ $t("ativo") }}</b-tag
                                >
                                <b-tag
                                    class="table-tag table-tag-full-width"
                                    v-else
                                    type="is-danger"
                                    size="is-medium"
                                    >{{ $t("inativo") }}</b-tag
                                >
                            </div>
                            <div v-else>
                                {{ props.row[f.key] }}
                            </div>
                        </b-table-column>
                    </b-table>
                    <PaginationViewer
                        class="mb-0"
                        :resetPagination="pagination.resetPagination"
                        :totalItems="pagination.totalRows"
                        :perPageOptions="pagination.perPageOptions"
                        :optionStarted="pagination.optionStarted"
                        @change="updatePagination"
                    />
                </div>
            </div>
        </div>
        <router-view />
    </div>
</template>

<script>
import OcorrenciaService from "@/services/agro/ocorrencia/OcorrenciaService";
import TipoOcorrenciaService from "@/services/agro/ocorrencia/TipoOcorrenciaService";
import ContainerLoader from "@/components/content/loader/ContainerLoader";
import { mapState } from "vuex";

export default {
    beforeRouteUpdate(to, from, next) {
        if (to.name.includes("ocorrencia") && !to.name.includes("ocorrencia-list")) {
            this.rotaAninhada = true;
            next();
        } else {
            this.rotaAninhada = false;
            this.carregarDados(this.pagination.currentPage);
            next();
        }
    },
    components: {
        ContainerLoader,
    },
    data() {
        return {
            rotaAninhada: false,
            integracao: this.$store.state.user.tabelasERPWebServer.includes("AGRO_OCORRENCIA"),
            ocorrencias: [],
            situacaoSelect: [
                { id: 2, label: "todos" },
                { id: 1, label: "ativos" },
                { id: 0, label: "inativos" },
            ],
            situacaoSelected: { id: 2, label: "todos" },
            service: new OcorrenciaService(this.$http, this.$router),
            serviceTipoOcorrencia: new TipoOcorrenciaService(this.$http, this.$router),
            fields: [
                { key: "codigo", width: '10%', sortable: false, sortFn: null },
                { key: "nomepopular", width: null, sortable: true, sortFn: null },
                { key: "nomecientifico", width: null, sortable: true, sortFn: null },
                { key: "tipo", width: null, sortable: false, sortFn: null },
                { key: "situacao", width: 70, sortable: false, sortFn: null },
                { key: "excluir", width: 20, sortable: false, sortFn: null },
            ],
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
            isLoading: false,
            isEmpty: false,
            debouncedOnSearch: null,
            delete: null,
            queryFilter: "",
            column: "nome",
            order: "asc",
            permissao: {},
            tipoSelect: [{ id: 0, label: this.$t('todos') }],
            tipoSelected: { id: 0, label: this.$t('todos') },

            abrev: this.$route.meta.contexto.slice(0,3),
            isVenda: this.$route.meta.contexto == "vendas",
            isAgro: this.$route.meta.contexto == "agro",
        };
    },
    created() {
        if (this.$route.name.includes("ocorrencia") && !this.$route.name.includes("ocorrencia-list")) {
            this.rotaAninhada = true;
        }else{
            this.rotaAninhada = false;
        }

        this.pagination.perPage =
            this.pagination.perPageOptions[this.pagination.optionStarted];

        if (this.isAgro) {
            this.abrev = this.$route.meta.contexto.slice(0,4);
            this.permissao = this.hasPermissionAccess(
                "agro",
                "agro-cadastros",
                "agro-ocorrencia"
            );
        }else if (this.isVenda) {
            this.permissao = this.hasPermissionAccess(
                "vendas",
                "crm",
                "ven-ocorrencia"
            );
        }

        if (!this.permissao.read) {
            this.$router.push({
                name: "acesso-nao-permitido",
                params: { tela: "ocorrencia" },
            });
        } else {
            this.debouncedOnSearch = this.debounce(this.onSearch, 500);
            this.carregarDados();
            this.loadTipo();
        }
    },
    computed: {
        ...mapState(["hide"]),
    },
    methods: {
        infoIsHidden() {
            this.$store.dispatch("infoIsHidden", {
                infoIsHidden: !this.hide.infoIsHidden,
            });
        },
        onSearch(value) {
            this.queryFilter = this.removeAcentos(value);
            this.pagination.currentPage = 1;
            this.carregarDados();
        },
        onSituacaoChanged(val) {
            this.situacaoSelected = val
                ? this.situacaoSelect.filter((v) => v.id == val.id)[0]
                : this.situacaoSelect[0];
            this.carregarDados();
        },
        onTipoChanged(val) {
            this.tipoSelected = val
                ? this.tipoSelect.filter((v) => v.id == val.id)[0]
                : this.tipoSelect[0];
            this.carregarDados();
        },
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        carregarDados(currentPage = 1) {
            if (currentPage == 1 && this.pagination.currentPage != 1)
                this.pagination.resetPagination = true;
            this.isLoading = true;

            let params = [ this.queryFilter!=''?this.queryFilter:'.*', this.tipoSelected.id  ];

            this.service
                .findAll(
                    this.column,
                    this.order,
                    currentPage - 1,
                    this.pagination.perPage,
                    this.situacaoSelected.id,
                    this.abrev,
                    params
                )
                .then(this.mapItems)
                .then((value) => {
                    this.ocorrencias = value;
                    this.isEmpty = this.ocorrencias.length <= 0;
                })
                .catch(e => {
                    this.isEmpty = true;
                    this.$root.showMessage("error", "msg-erro-load");
                    console.error("Erro ao carregar os dados.");
                })
                .finally(_ => {
                    this.isLoading = false;
                    this.pagination.resetPagination = false;
                });
        },
        mapItems(body) {
            this.pagination.totalRows = body.totalElements;
            return body.content.map((m) => {
                return {
                    id: m.id,
                    codigo: m.idagro_ocorrenciaerp,
                    nomepopular: m.nomepopular,
                    nomecientifico: m.nomecientifico,
                    tipo: m.agroTipoOcorrencia.descricao,
                    situacao: m.situacao,
                    excluir: "excluir",
                };
            });
        },
        sortTable(field, order) {
            this.column = field;
            this.order = order;
            this.carregarDados();
        },
        promptChangeSituacao(row) {
            this.delete = row;
            let message = this.$t(
                    row.situacao == 1
                        ? "msg-confirmacao-desativacao-n"
                        : "msg-confirmacao-reativacao-n", 
                    { n: row.nomepopular }
                );
            this.$root.confirm({
                message: message,
                onConfirm: _ => this.updateSituacaoRegistro(),
                onCancel: _ => this.removePrompt(),
            });
        },
        removePrompt() {
            this.delete = null;
        },
        updateSituacaoRegistro() {
            let tempFailure =
                this.delete.situacao == 1
                    ? "msg-erro-ao-desativar-ocorrencia"
                    : "msg-erro-ao-reativar-ocorrencia";

            let tempOcorrencia = {
                idagro_ocorrencia: this.delete.id,
                situacao: this.delete.situacao == 1 ? 0 : 1,
            };

            this.isLoading = true;

            this.service
                .updateSituacao(tempOcorrencia, this.abrev)
                .then((r) => {
                    if (r.status == 200) {
                        let tempSuccess =
                            r.content.situacao == 1
                                ? "msg-sucesso-ao-reativar-ocorrencia"
                                : "msg-sucesso-ao-desativar-ocorrencia";
                        this.$root.showMessage("success", tempSuccess);
                        this.carregarDados(this.pagination.currentPage);
                    } else if (r.status == 500) {
                        this.$root.showMessage("error", "Erro ao atualizar status.");
                    }
                })
                .catch((e) => {
                    console.error("Erro ao atualizar status.", e);
                    this.$root.showMessage("error", tempFailure);
                })
                .finally((_) => {
                    this.isLoading = false;
                    this.delete = null;
                });
        },
        loadTipo() {
            this.serviceTipoOcorrencia
                .findAllCombo("", this.abrev)
                .then(body => {
                return body.content.map(m => {
                    return { 
                        id: m.id, 
                        label: m.descricao,
                        cor: m.cor,
                        icone: m.icone.toLowerCase(),
                    };
                });
        })
        .then(tipos => {
            this.tipoSelect.push.apply(this.tipoSelect, tipos);
        })
        .catch(e => {
            console.error(e);
        });
    },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: 100%;

    .page {
        position: relative;
        padding-left: 0;
    }
}
</style>